import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
// import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import { Button, IconButton } from "@mui/material";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import HomeIcon from "@mui/icons-material/Home";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { BsSearch } from "react-icons/bs";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import DownloadIcon from "@mui/icons-material/Download";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ClearIcon from "@mui/icons-material/Clear";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import { rankingSorter, CustomPagination, CustomNoRowsOverlay } from "../../../SecondaryFunctions";
import isAuthenticated from "../../../../components/Utils/isAuthenticated";
import { getUSRIReportData } from "../../../../components/Api/Getters";
import ReportDialog from "./ReportDialog";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { Close } from "@mui/icons-material";

const Row = styled(Paper)(({ theme }) => ({
	backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
	...theme.typography.body2,
	padding: theme.spacing(1),
	boxShadow:
		theme.palette.mode === "dark"
			? "0px 2px 4px rgba(0, 0, 0, 0.4)"
			: "0px 2px 4px rgba(0, 0, 0, 0.2)",
	marginBottom: "3px",
	marginTop: "3px",
}));

// mappings for different reference groups
const ref_options = {
	C: "All Courses",
	CS: "All Courses Similar Class Size",
	CL: "All Courses Similar Course Level",
	CSL: "All Courses Similar Course Level/Class Size",
	F: "Courses in Faculty",
	FS: "Courses in Faculty Similar Class Size",
	FL: "Courses in Faculty Similar Course Level",
	FSL: "Courses in Faculty Similar Course Level/Class Size",
	D: "Courses in Department",
	DS: "Course in Department Similar Class Size",
	DL: "Courses in Department Similar Class Level",
	DSL: "Courses in Department Similar Class Level/Class Size",
};

export default function MyUSRIReports() {
	// Define various state variables using the useState hook
	const auth = isAuthenticated();
	const token = auth.token;
	const empid = auth.empid;
	const [loaded, setLoaded] = useState(false); // To track if data is loaded
	const [loadingBar, setLoadingBar] = useState(true); // To show/hide loading bar
	const [apiFetched, setApiFetched] = useState(false); // To track API data fetching
	const [courseData, setCourseData] = useState([]); // Store fetched course data
	const [filteredCourseData, setFilteredCourseData] = useState([]); // Store filtered course data
	const [filters, setFilters] = useState({
		faculty: [],
		department: [],
		acyear: [],
		term: [],
	}); // Store filter values
	const [courseName, setCourseName] = useState(""); // Store course name for search
	const [selectedData, setSelectedData] = useState([]); // Store selected row data
	const [openWindow, setOpenWindow] = useState(false); // Control the visibility of a dialog window
	const [pdfLoading, setPdfLoading] = useState(false);
	const [openReportDialog, setOpenReportDialog] = useState(false); // Control the visibility of the report dialog
	const [reportData, setReportData] = useState([]); // Store report data
	const [progress, setProgress] = useState({ completed: 0, total: 0 });

	const columns = [
		{
			field: "course",
			headerName: "Course",
			headerAlign: "center",
			flex: 0.34,
			renderCell: (params) => (
				<div className="flex items-center justify-center h-full">{params.value}</div>
			),
		},
		{
			field: "description",
			headerName: "Description",
			headerAlign: "center",
			flex: 0.33,
			renderCell: (params) => (
				<div className="flex items-center justify-center h-full">{params.value}</div>
			),
		},
		{
			field: "term",
			headerName: "Term",
			headerAlign: "center",
			flex: 0.33,
			renderCell: (params) => (
				<div className="flex items-center justify-center h-full">{params.value}</div>
			),
		},
	];
	const chunkArray = (arr, size) => {
		const chunks = [];
		for (let i = 0; i < arr.length; i += size) {
			chunks.push(arr.slice(i, i + size));
		}
		return chunks;
	};
	const fetchChunkedData = async (allReqnums, type, action = "view") => {
		const chunkSize = 4;
		const chunks = chunkArray(allReqnums, chunkSize);
		// set total = total number of items to fetch
		setProgress({ completed: 0, total: allReqnums.length });
		const accumulated = [];

		for (let i = 0; i < chunks.length; i++) {
			const chunk = chunks[i];
			try {
				const response = await getUSRIReportData(token, chunk, type, action);
				if (response?.data) {
					accumulated.push(...response.data);
				}
			} catch (err) {
				console.error(`chunk ${i} failed`, err);
			} finally {
				// increment "completed" by the size of this chunk
				setProgress((p) => ({
					...p,
					completed: p.completed + chunk.length,
				}));
			}
		}

		return accumulated;
	};
	// Function to open the dialog window for generating reports
	const handleClickOpen = () => {
		if (selectedData.length > 0) {
			setOpenWindow(true);
		}
	};

	// Function to close the dialog window
	const handleClose = () => {
		setOpenWindow(false);
	};

	// Function to generate and download merged PDF report
	const viewReports = async () => {
		setPdfLoading(true);
		const reqnumClsNums = selectedData.map(
			(item) => `${item.reqnum}-${item.clsnum}-${item.ref_group}`,
		);
		try {
			const allData = await fetchChunkedData(reqnumClsNums, "indiv", "view");
			if (allData.length) {
				setReportData(allData);
				setPdfLoading(false);
				setOpenReportDialog(true);
			} else {
				setPdfLoading(false);
				alert("An error occurred while generating the report. Please try again or contact TSQS.");
			}
		} catch (error) {
			setPdfLoading(false);
			alert("An error occurred while generating the report. Please try again or contact TSQS.");
		}
	};

	const generateReport = async () => {
		setPdfLoading(true);
		const reqnumClsNums = selectedData.map(
			(item) => `${item.reqnum}-${item.clsnum}-${item.ref_group}`,
		);
		const allData = await fetchChunkedData(reqnumClsNums, "indiv", "download");
		console.log("allData", allData);
		try {
			if (allData.length) {
				handleDownloadAllReports(allData);
				setPdfLoading(false);
			} else {
				setPdfLoading(false);
				alert("An error occurred while generating the report. Please try again or contact TSQS.");
			}
		} catch (error) {
			setPdfLoading(false);
			alert("An error occurred while generating the report. Please try again or contact TSQS.");
		}
	};

	const pageWidth = 297; // mm
	const pageHeight = 210; // mm
	const marginLeft = 10,
		marginTop = 10,
		marginRight = 10;

	const headerHeight = 25; // space reserved for header

	const handleDownloadAllReports = (data) => {
		console.log("data for pdf", data);
		const fileName = `USRI Report.pdf`;
		const doc = new jsPDF("l", "mm", "a4");
		data.forEach((data, index) => {
			const { header, pub_results, indiv_results, chair_results, labels, class_data } = data;
			const results = pub_results || indiv_results || chair_results;

			const reqNumKey = header?.requisition?.split("-")[0];
			const ratingRows = results?.[reqNumKey][0].rating_items || [];
			if (index !== 0 && ratingRows.length > 0) {
				doc.addPage();
			}

			const comments = results?.[reqNumKey][0].comments;

			function drawHeader(doc, headerData, pageWidth, startY = marginTop) {
				// Set fonts and sizes as needed

				doc.setFont("roboto", "normal");
				doc.setFontSize(10);
				const ctitle = header?.class_title.trim();
				const instructor = header?.instructor.trim();
				const reportDate = header?.report_date.trim();
				const department = header?.department.trim();
				const faculty = header?.faculty.trim();
				const academicYear = header?.academic_year.trim();
				const catalog = header?.catalog.trim();
				const requisition = header?.requisition.trim();
				doc.text("Instructor: ", marginLeft, startY);
				doc.text("Class: ", marginLeft, startY + 5);
				doc.text("Department: ", marginLeft, startY + 10);
				doc.text("Faculty: ", marginLeft, startY + 15);
				doc.text(reportDate, 285, startY, { align: "right" });
				doc.text("Academic Year: ", 273, startY + 5, { align: "right" });
				doc.text("Catalog: ", 277, startY + 10, { align: "right" });
				doc.text("Requisition Number: ", 268, startY + 15, { align: "right" });

				doc.setFont("roboto", "bold");
				doc.text(instructor, 26, startY);
				doc.text(ctitle, 20, startY + 5);
				doc.text(department, 29, startY + 10);
				doc.text(faculty, 23, startY + 15);
				doc.text(academicYear, 285, startY + 5, { align: "right" });
				doc.text(catalog, 285, startY + 10, { align: "right" });
				doc.text(requisition, 285, startY + 15, { align: "right" });

				doc.setFont("roboto", "normal");
				doc.setFontSize(10);

				// Center title
				doc.setFont("roboto", "bold");
				doc.setFontSize(12);
				doc.text("Universal Student Ratings of Instruction", pageWidth / 2, startY, {
					align: "center",
				});

				// Reset font for content
				doc.setFont("roboto", "normal");
				doc.setFontSize(10);
				doc.rect(marginLeft, 27, pageWidth - marginLeft - marginRight, 0.1); // Draw a line under the header

				// Return the next available Y coordinate after the header
				return startY + headerHeight;
			}

			function drawCommentsSection(doc, comments, currentY, availableHeight, headerData) {
				// Force new page for comments:
				doc.addPage();
				currentY = drawHeader(doc, headerData, pageWidth);
				doc.setFont("roboto", "bold");
				doc.text("USRI COMMENTS", pageWidth / 2, currentY, { align: "center" });
				doc.setFont("roboto", "normal");
				currentY += 7;

				Object.entries(comments).forEach(([key, commentArray]) => {
					const parts = key.replace(/[()]/g, "").split(",");

					const itemNumber = parts[1]?.trim();
					// remove quotes from the item text
					const itemText = parts
						.slice(2)
						.join(",")
						.trim()
						// eslint-disable-next-line no-control-regex
						.replace(/['"\\\x00]/g, "");

					// get rid of \x00 characters

					const normalText = "Comment Item Number " + itemNumber + ": ";
					const boldText = itemText;
					const headerLineHeight = 5;

					if (currentY + headerLineHeight > availableHeight) {
						doc.addPage();
						currentY = drawHeader(doc, headerData, pageWidth);
					}
					// Draw the header text in two parts:
					doc.setFont("roboto", "normal");
					doc.text(normalText, marginLeft, currentY);
					const normalTextWidth = doc.getTextWidth(normalText);
					doc.setFont("roboto", "bold");
					doc.text(boldText, marginLeft + normalTextWidth, currentY);
					// Draw a line under the header
					doc.rect(marginLeft, currentY + 1, pageWidth - marginLeft - marginRight, 0.1);
					// Reset font back to normal for subsequent text
					doc.setFont("roboto", "normal");
					currentY += headerLineHeight + 1;

					commentArray.forEach((comment) => {
						// Calculate maximum width for comment text
						const maxWidth = pageWidth - marginRight - (marginLeft + 5);
						// Split comment text into lines that fit within maxWidth
						const splittedComment = doc.splitTextToSize(comment, maxWidth);
						const lineHeight = 5;
						const totalCommentHeight = splittedComment.length * lineHeight;

						if (currentY + totalCommentHeight > availableHeight) {
							doc.addPage();
							currentY = drawHeader(doc, headerData, pageWidth);
							doc.setFont("roboto", "bold");
							doc.text("USRI COMMENTS (CONTINUED)", pageWidth / 2, currentY, {
								align: "center",
							});
							currentY += 7;
							// Write comment header for continuation using mixed fonts
							const normalTextCont = "Comment Item Number " + itemNumber + " (continued): ";
							const boldTextCont = itemText;
							doc.setFont("roboto", "normal");
							doc.text(normalTextCont, marginLeft, currentY);
							const normalTextWidthCont = doc.getTextWidth(normalTextCont);
							doc.setFont("roboto", "bold");
							doc.text(boldTextCont, marginLeft + normalTextWidthCont, currentY);
							doc.rect(marginLeft, currentY + 1, pageWidth - marginLeft - marginRight, 0.1);
							doc.setFont("roboto", "normal");
							currentY += headerLineHeight + 1;
						}

						// Draw the comment text
						doc.text(splittedComment, marginLeft + 5, currentY);
						currentY += totalCommentHeight;

						// only add dotted line if not the last comment in the array
						if (comment !== commentArray[commentArray.length - 1]) {
							// Set a dotted line style: [dashLength, gapLength]
							doc.setLineDash([1, 2]);
							doc.setDrawColor(150); // Set color for the line (optional)
							// Draw the horizontal dotted line across the comment area
							doc.line(
								marginLeft + 5,
								currentY - lineHeight / 2,
								pageWidth - marginRight,
								currentY - lineHeight / 2,
							);
							// Reset line dash to solid (optional)
							doc.setLineDash([]);

							// Add extra spacing after the dotted line
							currentY += 2;
							doc.setDrawColor(0); // Reset color to default
						}
					});

					// Add extra spacing after each comment group
					currentY += 3;
				});
				return currentY;
			}

			const headerData = {
				instructor: header?.instructor.trim(),
				class_title: header?.class_title.trim(),
				department: header?.department.trim(),
				faculty: header?.faculty.trim(),
				report_date: header?.report_date.trim(),
				academic_year: header?.academic_year.trim(),
				catalog: header?.catalog.trim(),
				requisition: header?.requisition.trim(),
			};

			const head = [
				[
					{ content: "ITEM", rowSpan: 2, styles: { halign: "left" } },
					{
						content: "RESPONSES FROM THIS CLASS",
						colSpan: 5,
						styles: { halign: "center" },
					},
					{ content: "CLASS MEDIAN", rowSpan: 2, styles: { halign: "center" } },
					{
						content: "RANKS OF MEDIANS FROM OTHER CLASSES",
						colSpan: 5,
						styles: { halign: "center" },
					},
				],
				[
					{ content: "SD", styles: { halign: "center" } },
					{ content: "D", styles: { halign: "center" } },
					{ content: "N", styles: { halign: "center" } },
					{ content: "A", styles: { halign: "center" } },
					{ content: "SA", styles: { halign: "center" } },
					{ content: "Tukey Fence", styles: { halign: "center" } },
					{ content: "25th", styles: { halign: "center" } },
					{ content: "50th", styles: { halign: "center" } },
					{ content: "75th", styles: { halign: "center" } },
					{ content: "Total Classes", styles: { halign: "center" } },
				],
			];

			const body = ratingRows.map((row) => {
				// For rows with custom reference type, show a single cell spanning the 5 columns for ranks.
				if (row.ref_type === "custom") {
					return [
						row.item_text,
						row.rsp1,
						row.rsp2,
						row.rsp3,
						row.rsp4,
						row.rsp5,
						row.median,
						{
							content: "no data available",
							colSpan: 5,
							styles: { halign: "center" },
						},
					];
				} else if (row.ref_type === "too_few") {
					return [
						row.item_text,
						row.rsp1,
						row.rsp2,
						row.rsp3,
						row.rsp4,
						row.rsp5,
						row.median,
						{
							content: "too few uses for rating",
							colSpan: 5,
							styles: { halign: "center" },
						},
					];
				} else {
					// For standard rows, split the rank data accordingly.
					const refParts = row.ref_data ? row.ref_data.split("   ") : [];
					return [
						row.item_text,
						row.rsp1,
						row.rsp2,
						row.rsp3,
						row.rsp4,
						row.rsp5,
						row.median,
						refParts[0] || "",
						refParts[1] || "",
						refParts[2] || "",
						refParts[3] || "",
						// Assume total classes comes from a different split (adjust as needed)
						row.ref_data ? row.ref_data.split("    ")[1] || "" : "",
					];
				}
			});
			const foot = [
				[
					{
						content: `Number of students responding to questionnaire: ${class_data.qtally} out of ${
							class_data.csize
						} (${((Number(class_data.qtally) / Number(class_data.csize)) * 100).toFixed(1)}%)`,
						colSpan: 12,
						styles: { halign: "left", cellPadding: { top: 4, left: 1 } },
					},
				],
				[
					{
						content:
							class_data.webct === "Yes"
								? "Responses from this class were collected online."
								: "Responses from this class were collected in person.",
						colSpan: 12,
						styles: { halign: "left" },
					},
				],
				[
					{
						content: labels.label,
						colSpan: 12,
						styles: { halign: "left", cellPadding: { top: 2, left: 1 } },
					},
				],
				[
					{
						content:
							"The reference data for the Universal Items is based on results beginning with Academic Year 2005/06 while other items use all data available.",
						colSpan: 12,
						styles: { halign: "left" },
					},
				],
				[
					{
						content:
							"Information about the contents of this report may be found on the Web at https://www.ualberta.ca/information-services-and-technology/services/test-scoring-questionnaire-services/student-perspectives-of-teaching.html",
						colSpan: 12,
						styles: { halign: "left" },
					},
				],
			];
			let currentY;

			if (ratingRows.length > 0) {
				currentY = drawHeader(doc, headerData, pageWidth);

				autoTable(doc, {
					head: head,
					body: body,
					foot: foot,
					// Set margins to leave space for your custom header (if drawn separately)
					margin: {
						top: headerHeight + marginTop - 5,
						left: marginLeft,
						right: marginRight,
					},
					styles: {
						font: "roboto",
						fontSize: 8,
						cellPadding: 1,
						overflow: "linebreak",
						halign: "left",
						valign: "middle",
						lineWidth: 0.1,
					},
					headStyles: {
						fillColor: [255, 255, 255], // no header shading
						textColor: [0, 0, 0],
						lineColor: [200, 200, 200],
					},
					// Override body styling (optional if you want alternating row shading)
					bodyStyles: {
						fillColor: [255, 255, 255], // no shading
						textColor: [0, 0, 0],
						lineColor: [200, 200, 200],
					},
					// Optionally add very light alternating row shading to help parse columns
					alternateRowStyles: {
						fillColor: [245, 245, 245],
					},
					// Override footer styling to remove cell lines/dividers
					footStyles: {
						fillColor: [255, 255, 255],
						textColor: [0, 0, 0],
						lineWidth: 0, // no borders in the footer
						/* padding: { top: 1, bottom: 1, left: 2, right: 2 }, */
					},
					columnStyles: {
						0: { cellWidth: 160 }, // force ITEM column to be 50 mm wide
						1: { halign: "center" },
						2: { halign: "center" }, // force RESPONSES FROM THIS CLASS column to be 20 mm wide
						3: { halign: "center" }, // force RESPONSES FROM THIS CLASS column to be 20 mm wide
						4: { halign: "center" }, // force RESPONSES FROM THIS CLASS column to be 20 mm wide
						5: { halign: "center" }, // force RESPONSES FROM THIS CLASS column to be 20 mm wide
						6: {
							cellWidth: 20,
							//align center
							halign: "center",
						}, // force RESPONSES FROM THIS CLASS column to be 20 mm wide
						7: { cellWidth: 14, halign: "center" }, // force CLASS MEDIAN column to be 20 mm wide
						8: { cellWidth: 10, halign: "center" }, // force 25th column to be 20 mm wide
						9: { cellWidth: 10, halign: "center" }, // force 50th column to be 20 mm wide
						10: { cellWidth: 10, halign: "center" }, // force 75th column to be 20 mm wide
						11: { cellWidth: 18, halign: "center" }, // force Total Classes column to be 20 mm wide
					},
					theme: "grid",
					didDrawCell: function (data) {
						if (data.section === "foot" && data.row.index === 0) {
							doc.setDrawColor(200); // Set color for the line (optional)
							data.doc.line(marginLeft, data.cell.y, pageWidth - marginRight, data.cell.y);
						}
					},
				});

				currentY += doc.lastAutoTable.finalY + 10; // Adjust for the height of the table
			}
			const marginBottom = 20; // mm

			// If comments exist, add them on a new page:

			if (comments && Object.keys(comments).length > 0) {
				drawCommentsSection(doc, comments, currentY, pageHeight - marginBottom, headerData);
			}
			// Add a page break if not the last report
		});

		doc.save(fileName);
	};

	// Function to handle Enter key press for course search
	const handleKeypress = (e) => {
		if (e.keyCode === 13) {
			searchCourse();
		}
	};

	// Function to search for courses based on course name
	function searchCourse() {
		let temp = filteredCourseData.filter((item) => item.course.includes(courseName));
		setFilteredCourseData(temp);
	}

	// Function to clear search and reset filteredCourseData
	function handleClear() {
		let clearedName = "";
		setCourseName(clearedName);
		applyFilters(clearedName);
		// setCourseName("");
		// let temp = courseData.filter((item) => item.course.includes(""));
		// setFilteredCourseData(temp);
	}

	// Function to update filter values
	function updateFilters(fgroup, value, checked) {
		// Create a copy of the filters object
		let updatedFilters = filters;

		// Check if the item should be added to the filters
		if (checked) {
			// Add the value to the filter group if it doesn't already exist
			if (!updatedFilters[fgroup].includes(value)) {
				updatedFilters[fgroup].push(value);
			}
		} else {
			// Remove the value from the filter group if it exists
			updatedFilters[fgroup] = updatedFilters[fgroup].filter((item) => item !== value);
		}

		// Update the filters state with the updated filters object
		setFilters(updatedFilters);
		// Apply filters to update filteredCourseData
		applyFilters(courseName);
	}

	// Function to apply selected filters and update filteredCourseData
	function applyFilters(filteredName) {
		// Array of filter categories
		const filterCategories = ["faculty", "department", "acyear", "term"];

		// Initialize filteredData as an empty array
		let filteredData = [];
		let courseData_copy = courseData.filter((item) => item.course.includes(filteredName));
		// Iterate over each filter category
		filterCategories.forEach((category) => {
			// Check if the current filter category has selected values
			if (filters[category].length !== 0) {
				// Filter courseData based on the selected values of the current category
				let temp = courseData_copy.filter((item) => filters[category].includes(item[category]));

				// If filteredData is empty, assign temp to it
				if (filteredData.length === 0) {
					filteredData = temp;
				} else {
					// Intersect filteredData with temp to get common elements
					filteredData = filteredData.filter((value) => temp.includes(value));
				}
			} else {
				// If no selected values for the current category, retain filteredData as it is
				// or intersect it with courseData
				if (filteredData.length === 0) {
					filteredData = courseData_copy;
				} else {
					filteredData = filteredData.filter((value) => courseData_copy.includes(value));
				}
			}
		});
		// Check if filteredData is not empty
		if (filteredData.length !== 0) {
			// Sort filteredData based on 'termNum' and 'ctitle' properties using the
			// 'rankingSorter' function
			filteredData.sort(rankingSorter("termNum", "ctitle"));

			// Set the filtered course data using the updated filteredData
			setFilteredCourseData(filteredData);
		}
	}

	// useEffect hook to fetch and process data from the API
	useEffect(() => {
		let url = process.env.REACT_APP_BACKEND_HOST.concat("/spot/usri_classes/");
		async function fetch_results_by_empid(empid) {
			const response = await fetch(url.concat(empid, "/"), {
				headers: { Authorization: `Token ${token}` },
			});
			const json = await response.json();

			// Process the fetched data and create an array of objects
			let apiData = [];
			for (let i = 0; i < json.length; i++) {
				let entry = {
					id: i,
					reqnum: json[i].REQNUM,
					clsnum: json[i].CLSNUM,
					faculty: json[i].FNAME.trim(),
					department: json[i].DNAME.trim(),
					description: json[i].DESCRIPTION,
					acyear: json[i].ACYEAR.trim(),
					term: json[i].DESC_TERM.trim(),
					termNum: json[i].TERM,
					classid: json[i].CLASSID,
					course: json[i].CTITLE.trim(),
					ctype: json[i].CTYPE,
					empid: json[i].EMPID,
					name: json[i].NAME.trim(),
					report_id: json[i].REPORT_ID,
					ref1: json[i].REF1.trim(),
					ref2: json[i].REF2.trim(),
					ref3: json[i].REF3.trim(),
					ref_group: json[i].REF1.trim(),
				};

				apiData.push(entry);
			}
			// Set the state to indicate API data has been fetched
			setApiFetched(true);
			return apiData;
		}
		// Fetch data if not loaded
		if (!loaded) {
			let apiData = [fetch_results_by_empid(empid)];
			if (apiFetched) {
				Promise.all(apiData).then((values) => {
					let data = values[0];
					// Sort the fetched data based on 'termNum' and 'ctitle'
					data.sort(rankingSorter("termNum", "ctitle"));
					setCourseData(data);
					setFilteredCourseData(data);
				});
				setLoaded(true);
			}
		}
		// Show loading bar if loaded and still loading
		if (loaded && loadingBar) {
			setTimeout(() => {
				setLoadingBar(false);
			}, 3000);
		}
	}, [loaded, courseData, filteredCourseData, loadingBar, apiFetched, empid, token]);

	const handleSelectionModelChange = (newSelection) => {
		let items = [];

		if (Array.isArray(newSelection)) {
			items = newSelection;
		} else if (newSelection?.type === "exclude") {
			if (newSelection?.ids.size > 0) {
				const excludedIds = new Set(newSelection.ids);
				items = new Set(
					filteredCourseData.filter((row) => !excludedIds.has(row.id)).map((row) => row.id),
				);
			} else {
				items = new Set(filteredCourseData.map((row) => row.id));
			}
		} else if (newSelection?.type === "include" && newSelection?.ids) {
			items = newSelection.ids;
		} else {
			items = [];
		}

		//convert idArray to an array of numbers FROM set
		if (items instanceof Set) {
			items = Array.from(items).map((id) => Number(id));
		}

		// Map ids → actual row objects
		const selectedRows = filteredCourseData.filter((row) => items.includes(Number(row.id)));

		setSelectedData(selectedRows);
	};
	// Function to update the 'ref_group' value for a selected row
	function setRefGroup(index, value) {
		const updatedSelectedData = [...selectedData];
		updatedSelectedData[index].ref_group = value;
		setSelectedData(updatedSelectedData);
	}

	return (
		<div 
			style={{ 
				display: "flex", 
				flexDirection: "row", 
				height: "100vh",
				maxHeight: `calc(100vh - calc(13rem + 8px))`,
				backgroundColor: "#F5F5F5" 
			}} 
		>
			<ReportDialog
				open={openReportDialog}
				setOpen={setOpenReportDialog}
				reportData={reportData}
				reportType={"indiv"}
				handleDownloadAllReports={() => handleDownloadAllReports(reportData)}
			/>
			<div className="w-[21%] max-w-[400px] px-2  flex flex-col" >
				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						justifyContent: "center",
						marginY: "10px",
					}}
				>
					<TextField
						label="Course Title"
						size="small"
						value={courseName}
						onChange={(e) => {
							setCourseName(e.target.value.toUpperCase());
						}}
						onKeyUp={handleKeypress}
						sx={{ width: "322px", backgroundColor:'#FFF'}}
						InputProps={{
							endAdornment: (
								<IconButton onClick={handleClear}>
									{courseName.length > 0 ? <ClearIcon /> : ""}
								</IconButton>
							),
						}}
					/>

					<Button
						variant="contained"
						style={{
							backgroundColor: "#275d38",
							fontSize: "1.2rem",
							marginLeft: "2px",
						}}
						onClick={searchCourse}
					>
						<BsSearch />
					</Button>
				</Box>

				<Accordion disableGutters>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Typography>Faculty</Typography>
					</AccordionSummary>
					<AccordionDetails>
						{[...new Set(courseData.map((item) => item.faculty))].map((faculty, index) => (
							<FormControlLabel
								key={index}
								control={
									<Checkbox
										onChange={(e) => {
											updateFilters("faculty", e.target.value, e.target.checked);
										}}
									/>
								}
								value={faculty}
								label={faculty}
							/>
						))}
					</AccordionDetails>
				</Accordion>

				<Accordion disableGutters>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Typography>Department</Typography>
					</AccordionSummary>
					<AccordionDetails>
						{[...new Set(courseData.map((item) => item.department))].map((dept, index) => (
							<FormControlLabel
								key={index}
								control={
									<Checkbox
										onChange={(e) => {
											updateFilters("department", e.target.value, e.target.checked);
										}}
									/>
								}
								value={dept}
								label={dept}
							/>
						))}
					</AccordionDetails>
				</Accordion>

				<Accordion disableGutters>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Typography>Academic Year</Typography>
					</AccordionSummary>
					<AccordionDetails>
						{[...new Set(courseData.map((item) => item.acyear))].map((acyear, index) => (
							<FormControlLabel
								key={index}
								control={
									<Checkbox
										onChange={(e) => {
											updateFilters("acyear", e.target.value, e.target.checked);
										}}
									/>
								}
								value={acyear}
								label={acyear}
							/>
						))}
					</AccordionDetails>
				</Accordion>

				<Accordion disableGutters>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Typography>Term</Typography>
					</AccordionSummary>
					<AccordionDetails>
						{[...new Set(courseData.map((item) => item.term))].map((term, index) => (
							<FormControlLabel
								key={index}
								control={
									<Checkbox
										onChange={(e) => {
											updateFilters("term", e.target.value, e.target.checked);
										}}
									/>
								}
								value={term}
								label={term}
							/>
						))}
					</AccordionDetails>
				</Accordion>
			</div>
			<div className="p-2 w-[79%] flex flex-col h-[75dvh]">
				<DataGrid
					getRowHeight={() => "auto"}
					disableColumnFilter
					disableColumnSelector
					disableColumnMenu
					columns={columns}
					rows={filteredCourseData}
					pageSize={100}
					rowsPerPageOptions={[100]}
					checkboxSelection
					onRowSelectionModelChange={handleSelectionModelChange}
					density={"compact"}
					slots={{
						Pagination: CustomPagination,
						// LoadingOverlay: LinearProgress,
						LoadingOverlay: CircularProgress,
						NoRowsOverlay: CustomNoRowsOverlay,
					}}
					loading={loadingBar}
					sx={{ width: "100%" }}
				/>

				<div
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						marginTop: "15px",
						// marginBottom: "15px",
					}}
				>
					<Button
						style={{
							backgroundColor: "#275d38",
							width: "45%",
							maxWidth: "400px",
						}}
						variant="contained"
						endIcon={<DownloadIcon />}
						onClick={handleClickOpen}
					>
						<Typography style={{ fontSize: "16px" }}>View/Download Reports</Typography>
					</Button>
					<Dialog open={openWindow} onClose={handleClose} fullWidth={true} maxWidth={"xl"}>
						<Button
							disabled={pdfLoading}
							onClick={handleClose}
							sx={{ position: "absolute", right: 0, top: 10 }}
						>
							<Close />
						</Button>
						<DialogTitle sx={{ textAlign: "center", fontWeight: "bold" }}>USRI REPORT</DialogTitle>

						<div>
							<DialogContent>
								<div className="grid grid-cols-3 px-2  ">
									<div>
										<p className="font-bold ">TERM</p>
									</div>
									<div>
										<p className="font-bold ">COURSE</p>
									</div>
									<div>
										<p className="font-bold ">REFERENCE GROUP</p>
									</div>
								</div>
								<div className="flex flex-col gap-2">
									{selectedData.map((item, index) => (
										<div key={index}>
											<div className="grid grid-cols-3 py-2 px-4 border rounded items-center">
												<div>{item.term}</div>
												<div>{item.course}</div>
												<div>
													<FormControl sx={{ width: "80%" }} size="small">
														<Select
															value={item.ref_group}
															onChange={(e) => {
																setRefGroup(index, e.target.value);
															}}
														>
															{/* onChange={setRefGroup(index,value)}> */}
															<MenuItem value={item.ref1}>{ref_options[item.ref1]}</MenuItem>
															{item.ref2 !== "" && (
																<MenuItem value={item.ref2}>{ref_options[item.ref2]}</MenuItem>
															)}
															{item.ref3 !== "" && (
																<MenuItem value={item.ref3}>{ref_options[item.ref3]}</MenuItem>
															)}
														</Select>
													</FormControl>
												</div>
											</div>
										</div>
									))}
								</div>
							</DialogContent>

							<DialogActions sx={{ justifyContent: "center", position: "relative" }}>
								{pdfLoading ? (
									<Button disabled variant="contained" sx={{ backgroundColor: "#275d38" }}>
										<span className="mr-2">
											Generating reports {progress.completed}/{progress.total}…
										</span>
										<CircularProgress size={20} />
									</Button>
								) : (
									<>
										<Button onClick={viewReports}>View Report(s)</Button>
										<Button onClick={generateReport}>Download Report(s)</Button>
									</>
								)}
							</DialogActions>
						</div>
					</Dialog>
				</div>
			</div>
		</div>
	);
}
